import apiClient from './client';

export interface Appointment {
  id: number;
  patient?: {
    uuid: string;
    email: string;
    first_name: string;
    last_name: string;
    full_name: string;
    phone_number: string;
  } | null;
  guest_name: string;
  guest_phone: string;
  guest_email: string;
  professional: {
    id: number;
    user: {
      uuid: string;
      email: string;
      first_name: string;
      last_name: string;
      full_name: string;
    };
    specialty: string;
  };
  date: string;
  start_time: string;
  end_time: string;
  status: 'pending' | 'confirmed' | 'cancelled';
  whatsapp_confirmed: boolean;
  confirmation_token: string;
  confirmed_at: string | null;
  created_at: string;
  arrived: boolean;
  arrived_at: string | null;
}

export interface DailyStats {
  date: string;
  booked_count: number;
  total_slots: number;
  arrived_count: number;
  is_working_day: boolean;
}

export interface AppointmentCreateRequest {
  professional: number;
  date: string;
  start_time: string;
  end_time: string;
  patient?: number;  // For authenticated users
  guest_name?: string;
  guest_phone?: string;
   guest_email?: string;
  /** Client-generated id for idempotent offline replay -- see frontend/web/src/offline */
  client_uuid?: string;
}

export interface PaginatedAppointments {
  count: number;
  next: string | null;
  previous: string | null;
  results: Appointment[];
}

/**
 * Appointments API Service
 */
export const appointmentsAPI = {
  /**
   * Get all appointments for the authenticated user
   * (both as patient and as professional)
   */
  getMyAppointments: () =>
    apiClient.get<PaginatedAppointments>('/appointments/my_appointments/'),

  /**
   * Get all appointments (with optional filters)
   */
  getAll: (params?: { professional?: number; status?: string; date?: string }) =>
    apiClient.get<PaginatedAppointments>('/appointments/', { params }),

  /**
   * Get appointment by ID
   */
  getById: (id: number) =>
    apiClient.get<Appointment>(`/appointments/${id}/`),

  /**
   * Create a new appointment
   */
  create: (data: AppointmentCreateRequest) =>
    apiClient.post<Appointment>('/appointments/', data),

  /**
   * Update appointment
   */
  update: (id: number, data: Partial<AppointmentCreateRequest>) =>
    apiClient.patch<Appointment>(`/appointments/${id}/`, data),

  /**
   * Cancel appointment (soft delete)
   */
  cancel: (id: number) =>
    apiClient.delete(`/appointments/${id}/`),

  /**
   * Confirm appointment via WhatsApp token
   */
  confirm: (id: number, token: string) =>
    apiClient.post<Appointment>(`/appointments/${id}/confirm/`, { token, status: 'confirmed' }),

  /**
   * Mark patient as arrived
   */
  markArrived: (id: number, arrived: boolean) =>
    apiClient.post<Appointment>(`/appointments/${id}/mark_arrived/`, { arrived }),

  /**
   * Get daily appointment statistics
   */
  getDailyStats: (date?: string) =>
    apiClient.get<DailyStats>('/appointments/daily_stats/', { params: { date } }),
};
